import { getCustomProps } from '../forms'
import { getData } from '../get-table'
import { HttpService } from '../http.service'

const http = new HttpService()

export interface EndDate {
  endDate: string
}

export interface GetEndDate {
  licenseTypeId: string
  days: string
  startDate: string
}

export interface LicenseRequest {
  payrollId: string
  employeeId: string
  journeyId: string
  entityId: string
  licenseTypeId: string
  licenseNatureTypeId: string
  days: number
  startDate: string
  endDate: string
  statusId: string
  observation: string
  attachment: string
  isHistoric: boolean
  customFields: any
}

export interface IReverseLicense {
  licenseId: string
  observation: string
}

export async function getLicenseRequests(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = ''
): Promise<any> {
  // where's
  const where = ['license_requests.payroll_id=:payrollId']
  if (search) {
    where.push(
      `CONCAT(employees.code,' ',employees.name,' ',license_requests.start_date,' ',license_requests.end_date,' ',license_types.name,' ',entities.name,' ',request_status.code) ILIKE '%${search}%'`
    )
  }
  // order by
  let modifier = []
  dataOrder !== ''
    ? (modifier = [`order by ${dataOrder[1]} ${dataOrder[0]}`])
    : (modifier = ['order by license_requests.updated_at desc'])
  // result
  const response: any = await getData({
    tables: ['pg_license_requests'],
    fields: [
      'license_requests.id',
      'employees.code as employeeCode',
      'license_types.name as licenseTypeName',
      'entities.name as entity',
      "CONCAT(employees.code,' - ',employees.name) as employee",
      'request_status.code as status',
      'license_requests.days',
      "TO_CHAR(license_requests.start_date,'yyyy-mm-dd') as startDate",
      "TO_CHAR(license_requests.end_date,'yyyy-mm-dd') as endDate",
      'license_requests.updated_at',
      'license_requests.detail',
      'license_requests.origin'
    ],
    join: [
      'LEFT JOIN license_types ON license_requests.license_type_id=license_types.id',
      'LEFT JOIN entities ON license_requests.entity_id=entities.id',
      'LEFT JOIN employees ON license_requests.employee_id=employees.id',
      'LEFT JOIN request_status ON license_requests.status_id=request_status.id'
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response
}

export async function getLicenseRequest(id: string): Promise<LicenseRequest> {
  const data = await getData<any>({
    tables: ['pg_license_requests'],
    fields: [
      'license_requests.id',
      'license_requests.payroll_id as payrollId',
      'license_requests.employee_id as employeeId',
      'license_requests.journey_id as journeyId',
      'license_requests.entity_id as entityId',
      'license_requests.license_type_id as licenseTypeId',
      'license_requests.license_nature_type_id as licenseNatureTypeId',
      'license_requests.days',
      "TO_CHAR(license_requests.start_date,'yyyy-mm-dd') as startDate",
      "TO_CHAR(license_requests.end_date,'yyyy-mm-dd') as endDate",
      'license_requests.origin',
      'license_requests.observation',
      'license_requests.status_id as statusId',
      'license_requests.attachment',
      'license_requests.is_historic as isHistoric',
      'license_requests.custom_fields as custom'
    ],
    where: [`license_requests.id = '${id}'`],
    join: [],
    options: {
      outputFormat: 'json',
      pagination: { offset: 0, limit: 10 }
    }
  })
  return data[0].results[0]
}

export async function createLicenseRequest(
  data: LicenseRequest
): Promise<boolean> {
  const response: any = await http.post('license/license_request', {
    payrollId: data.payrollId,
    employeeId: data.employeeId,
    journeyId: data.journeyId,
    entityId: data.entityId,
    licenseTypeId: data.licenseTypeId,
    licenseNatureTypeId: data.licenseNatureTypeId,
    days: data.days,
    startDate: data.startDate,
    endDate: data.endDate,
    statusId: data.statusId,
    origin: 'APS_UI',
    observation: data.observation,
    attachment: data.attachment,
    isHistoric: data.isHistoric,
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function updateLicenseRequest(
  data: LicenseRequest,
  id: string
): Promise<boolean> {
  const response: any = await http.put('license/license_request/' + id, {
    payrollId: data.payrollId,
    employeeId: data.employeeId,
    journeyId: data.journeyId,
    entityId: data.entityId,
    licenseTypeId: data.licenseTypeId,
    licenseNatureTypeId: data.licenseNatureTypeId,
    days: data.days,
    startDate: data.startDate,
    endDate: data.endDate,
    statusId: data.statusId,
    origin: 'APS_UI',
    observation: data.observation,
    attachment: data.attachment,
    isHistoric: data.isHistoric,
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function getEndDate(data: GetEndDate): Promise<string> {
  const response: any = await http.post(
    'license/license_request/get_date_end',
    {
      licenseTypeId: data.licenseTypeId,
      days: data.days,
      startDate: data.startDate
    }
  )

  if (response?.data?.data?.error) {
    return response?.data?.data?.error
  }
  return response?.data?.endDate
}

export async function deleteLicenseRequest(id: string): Promise<boolean> {
  const response: any = await http.delete('license/license_request/' + id)

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function reverseLicense(data: IReverseLicense): Promise<any> {
  const response: any = await http.post(
    'license/license_request/' + data.licenseId + '/reverse',
    {
      observation: data.observation
    }
  )

  if (response?.data?.data?.error) {
    return response?.data?.data?.error
  }

  return response?.data
}

export async function getJourneyEmployee(id: string): Promise<any> {
  const response: any = await getData({
    tables: ['pg_employees'],
    fields: ['journey_id'],

    where: [`id='${id}'`],

    options: {
      outputFormat: 'json'
    }
  })

  return response[0].results[0].journey_id
}